import React from "react";
import { useNavigate } from "react-router-dom";
import { Typography, Box, Stack, Button, IconButton, CircularProgress } from "@mui/material";
import { ReactComponent as ArrowrightIcon } from "../../../../vendor/_new/icon/sites/arrow_right.svg";
import { ReactComponent as ArrowleftIcon } from "../../../../vendor/_new/icon/sites/arrow_left.svg";
import NetworkIcon from "@mui/icons-material/Dns";
import LinkIcon from "@mui/icons-material/Link";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import colors from "../../../../utils/colors";
import useSite from "../../../../hooks/user/useSite";
import useWAFConfig from "../../../../hooks/user/useWAFConfig";
import { containedButtonSx, outlinedButtonSx } from "../../../common/Button";
import copy from "copy-to-clipboard";
import { useNotification } from "../../../../contexts/NotificationContext";

const Finalize = ({ handlePrev }) => {
  const navigate = useNavigate();
  const { cursite, settingApply, onCreateSuccess, clearSites } = useSite();
  const { getEdgeConfig } = useWAFConfig();
  const { showSuccess } = useNotification();

  const [edgeConfig, setEdgeConfig] = React.useState({});
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    getEdgeConfig().then((res) => {
      setEdgeConfig(res);
    });
  }, [getEdgeConfig]);

  const apply = async () => {
    setLoading(true);
    const result = await settingApply(cursite?.site_id);
    setLoading(false);
    if (result.status === "success") {
      await onCreateSuccess(cursite?.site_id);
      clearSites();
      navigate("/v2/sites?refresh=" + Date.now());
    }
  };

  const handleCopy = (text) => {
    copy(text);
    showSuccess("Copied to clipboard");
  };

  const cardSx = {
    borderRadius: "8px",
    p: 4,
    border: `1px solid ${colors.neutral[200]}`,
    backgroundColor: "#FFFFFF",
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800], fontWeight: "medium", pb: 4 }}>
        Update <span style={{ color: colors.primary[300] }}>{cursite?.site_id}</span> DNS records to Layerd AI
      </Typography>

      <Stack direction="row" spacing={6}>
        <Stack spacing={4} sx={{ flex: 1 }}>
          <Stack sx={cardSx} spacing={3}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <NetworkIcon sx={{ width: 24, height: 24, color: colors.primary[300] }} />
              <Typography sx={ { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[800] }}>
                Layerd Anycast IP Addresses
              </Typography>
            </Stack>
            <Stack spacing={1}>
              {edgeConfig?.edge_a_records?.length > 0 ? (
                edgeConfig.edge_a_records.map((ip, index) => (
                  <Stack
                    key={index}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      backgroundColor: colors.neutral[100],
                      borderRadius: "4px",
                      px: 3,
                      py: 1.5,
                    }}
                  >
                    <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.primary[300] }}>
                      {ip}
                    </Typography>
                    <IconButton size="small" onClick={() => handleCopy(ip)} sx={{ p: 0 }}>
                      <ContentCopyIcon sx={{ width: 20, height: 20, color: colors.primary[300] }} />
                    </IconButton>
                  </Stack>
                ))
              ) : (
                <Box
                  sx={{
                    backgroundColor: colors.neutral[100],
                    borderRadius: "4px",
                    px: 3,
                    py: 2,
                  }}
                >
                  <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[500] }}>
                    Loading...
                  </Typography>
                </Box>
              )}
            </Stack>
          </Stack>

          <Stack sx={cardSx} spacing={3}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <LinkIcon sx={{ width: 24, height: 24, color: colors.primary[300] }} />
              <Typography sx={ { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[800] }}>
                Layerd CNAME Record
              </Typography>
            </Stack>
            <Stack spacing={1}>
              {edgeConfig?.edge_cname_records?.length > 0 ? (
                edgeConfig.edge_cname_records.map((cname, index) => (
                  <Stack
                    key={index}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      backgroundColor: colors.neutral[100],
                      borderRadius: "4px",
                      px: 3,
                      py: 1.5,
                    }}
                  >
                    <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.primary[300] }}>
                      {cname}
                    </Typography>
                    <IconButton size="small" onClick={() => handleCopy(cname)} sx={{ p: 0 }}>
                      <ContentCopyIcon sx={{ width: 20, height: 20, color: colors.primary[300] }} />
                    </IconButton>
                  </Stack>
                ))
              ) : (
                <Box
                  sx={{
                    backgroundColor: colors.neutral[100],
                    borderRadius: "4px",
                    px: 3,
                    py: 2,
                  }}
                >
                  <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[500] }}>
                    Loading...
                  </Typography>
                </Box>
              )}
            </Stack>
          </Stack>
        </Stack>

      </Stack>

      <Stack direction="row" alignItems="center" spacing={4} sx={{ pt: 6 }}>
        <Button
          startIcon={<ArrowleftIcon style={{ color: "#6371F7" }} />}
          onClick={handlePrev}
          disabled={loading}
          sx={{ height: 36, ...outlinedButtonSx }}
        >
          Back
        </Button>
        <Button
          endIcon={loading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <ArrowrightIcon style={{ color: "#fff" }} />}
          onClick={apply}
          disabled={loading}
          sx={{
            height: 36,
            ...containedButtonSx,
            "&.Mui-disabled": {
              backgroundColor: "#6371F7",
              borderColor: "#6371F7",
              color: "#fff",
              opacity: 0.7,
            },
          }}
        >
          {loading ? "Finishing..." : "Finish"}
        </Button>
      </Stack>
    </Box>
  );
};

export default Finalize;
